import * as React from 'react';
import { withApollo } from 'react-apollo';

import type { ApolloClient } from '@apollo/client';
import _ from 'lodash';
import { compose } from 'recompose';

import Imgix from 'js/components/Imgix';

import type { EventData } from '@coursera/event-pulse/sdk';
import { track } from '@coursera/event-pulse/sdk';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import StatusMessage from 'bundles/account-settings/components/common/StatusMessage';
import {
  EMAIL_PREFERENCE_LOGO_SIZE,
  Sections,
  UpdateStates,
} from 'bundles/account-settings/constants/SettingsConstants';
import type {
  CourseEmailPreference,
  CourseraNormalizedEmailPreference,
  EmailPreference,
  PartnerEmailPreference,
  UpdateStatus,
} from 'bundles/account-settings/types/EmailPreferences';
import Icon from 'bundles/iconfont/Icon';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/EmailPreferencesList';

function getPreferenceTypeLabels(): Record<string, string> {
  return {
    reminders: _t('Reminders'),
    announcements: _t('Announcements'),
    discussionForums: _t('Discussion Forums'),
  };
}

type PreferenceProps =
  | { type: typeof Sections.PARTNER_EMAIL_PREFERENCES; emailPreferences: PartnerEmailPreference[] }
  | { type: typeof Sections.COURSERA_EMAIL_PREFERENCES; emailPreferences: CourseraNormalizedEmailPreference[] }
  | { type: typeof Sections.COURSE_EMAIL_PREFERENCES; emailPreferences: CourseEmailPreference[] };

type PropsFromCaller = PreferenceProps & {
  disabled: boolean;
  updateStatus: UpdateStatus;
};

type PropsToComponent = PropsFromCaller & {
  client: ApolloClient<unknown>;
};

type State = {
  emailPreferences: EmailPreference[];
  clickedPreferences: { [key: string]: boolean }; // To track the clicked checkboxes on save
};

class EmailPreferencesList extends React.Component<PropsToComponent, State> {
  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);

    this.state = {
      emailPreferences: props.emailPreferences,
      clickedPreferences: {},
    };
  }

  static getDerivedStateFromProps(nextProps: PropsToComponent) {
    if (nextProps.updateStatus.state !== UpdateStates.UPDATING) {
      return {
        emailPreferences: nextProps.emailPreferences,
      };
    }

    return null;
  }

  componentDidMount(): void {
    this.trackPreferences('view_checkbox');
  }

  getTrackingData(preference: EmailPreference, type: string): EventData<'view_checkbox' | 'click_checkbox'> {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const checked = preference.value[type];

    switch (this.props.type) {
      case Sections.COURSE_EMAIL_PREFERENCES: {
        const preferenceTypeLabels = getPreferenceTypeLabels();
        return {
          checkbox: {
            name: 'enrolled_courses_communication_email_preference',
            label: preferenceTypeLabels[type],
            checked,
          },
          product: {
            type: 'course',
            id: preference.id,
            name: preference.name,
          },
        };
      }
      default:
        return {
          checkbox: {
            name: 'coursera_partners_communication_email_preference',
            label: preference.name,
            checked,
          },
        };
    }
  }

  trackPreferences(action: 'view_checkbox' | 'click_checkbox') {
    this.state.emailPreferences.forEach((preference) => {
      Object.keys(preference.value).forEach((type) => {
        if (action === 'view_checkbox' || this.state.clickedPreferences[`${preference.id}_${type}`]) {
          track(action, this.getTrackingData(preference, type));
        }
      });
    });
  }

  handleUpdate = () => {
    const { client } = this.props;

    if (!_.isEmpty(this.state.emailPreferences)) {
      SettingsActionCreators.updateEmailPreferences(this.props.type, this.state.emailPreferences, client);

      this.trackPreferences('click_checkbox');
      this.setState({ clickedPreferences: {} });
    }
  };

  handleSelect = (id: $TSFixMe, type: $TSFixMe, value: $TSFixMe) => {
    const updatedPreferences = this.state.emailPreferences;
    const updatedClickedPreferences = { ...this.state.clickedPreferences };

    const [preference] = _.filter(updatedPreferences, { id });

    if ('value' in preference) {
      if (!_.isEmpty(preference)) {
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        preference.value[type] = !value;
        updatedClickedPreferences[`${preference.id}_${type}`] = true;

        SettingsActionCreators.updatePendingChangesStatus(this.props.type, true);

        this.setState({
          emailPreferences: updatedPreferences,
          clickedPreferences: updatedClickedPreferences,
        });
      }
    }
  };

  /**
   * Renders check box for all preference options
   * @param preference
   * @return {XML} ReactElement
   */
  renderCheckbox(preference: $TSFixMe) {
    switch (this.props.type) {
      case Sections.COURSE_EMAIL_PREFERENCES:
        return _.map(preference.value, (value, type) => {
          const id = `${preference.id}~${type}`;
          const preferenceTypeLabels = getPreferenceTypeLabels();
          return (
            <div className="preference-item" key={id}>
              <input
                type="checkbox"
                className="preference-checkbox"
                checked={value}
                disabled={this.props.disabled}
                onChange={_.partial(this.handleSelect, preference.id, type, value)}
                id={id}
                name={id}
                data-id={preference.id}
                data-type={type}
              />
              <label className="preference-item-name" htmlFor={id}>
                {preferenceTypeLabels[type]}
              </label>
            </div>
          );
        });
      default:
        return _.map(preference.value, (value, type) => (
          <input
            key={`${preference.id}${type}`}
            type="checkbox"
            checked={!!value}
            onChange={_.partial(this.handleSelect, preference.id, type, value)}
            id={preference.id}
            name={preference.id}
            disabled={this.props.disabled}
            data-id={preference.id}
            data-type={type}
          />
        ));
    }
  }

  /**
   * Renders preference logo image
   * Because course logo is not square, using css to crop and make it square
   * @param {String} logo
   * @return {XML} ReactElement
   */
  renderLogo(logo: $TSFixMe) {
    const logoSize = EMAIL_PREFERENCE_LOGO_SIZE;
    const hasLogo = logo !== undefined;
    const blankLogo = <span className="logo no-logo" />;
    const styles = {
      background: 'url(' + logo + ') no-repeat center center',
      width: logoSize + 'px',
      height: logoSize + 'px',
    };

    switch (this.props.type) {
      case Sections.COURSE_EMAIL_PREFERENCES:
        if (!hasLogo) {
          return blankLogo;
        }

        return <span className="logo" style={styles} />;
      case Sections.PARTNER_EMAIL_PREFERENCES:
        if (!hasLogo) {
          return null;
        }
        return <Imgix className="logo" src={logo} maxWidth={logoSize} alt={Imgix.DECORATIVE} />;
      default:
        return <span />;
    }
  }

  /**
   * Renders text block for each preference row
   * @param preference
   * @return {XML} ReactElement
   */
  renderPreferenceText(preference: $TSFixMe) {
    return (
      <div className="preference-item-text">
        <h3 className="preference-item-name">{preference.name}</h3>
        {preference.description && <p className="preference-item-description">{preference.description}</p>}
      </div>
    );
  }

  renderSaveButton() {
    const { disabled } = this.props;

    let text: React.ReactNode = _t('Save');

    if (this.props.updateStatus.state === UpdateStates.UPDATING) {
      text = <Icon name="circle-o-notch" spin={true} />;
    }

    return (
      <button
        disabled={disabled}
        className="secondary cozy settings-primary-action"
        type="button"
        onClick={this.handleUpdate}
      >
        {text}
      </button>
    );
  }

  renderPreferenceItem(preference: $TSFixMe) {
    switch (this.props.type) {
      case Sections.COURSE_EMAIL_PREFERENCES:
        return (
          <div className="preference-item course-preference horizontal-box wrap">
            {this.renderLogo(preference.logo)}
            {this.renderPreferenceText(preference)}
            <div
              className="preference-checkbox-list"
              role="group"
              aria-label={_t('Communication from #{courseName}', { courseName: preference.name })}
            >
              {this.renderCheckbox(preference)}
            </div>
          </div>
        );
      default:
        return (
          <label className="preference-item" htmlFor={preference.id}>
            {this.renderCheckbox(preference)}
            {this.renderPreferenceText(preference)}
          </label>
        );
    }
  }

  getRoleAndLabel() {
    switch (this.props.type) {
      case Sections.COURSERA_EMAIL_PREFERENCES:
        return {
          'aria-label': _t('Communication from Coursera'),
        };
      case Sections.PARTNER_EMAIL_PREFERENCES:
        return {
          'aria-label': _t('Communication from Coursera partners'),
        };
      default:
        return {};
    }
  }

  render() {
    return (
      <div className="rc-EmailPreferencesList">
        <ul {...this.getRoleAndLabel()}>
          {_.map(this.state.emailPreferences, (preference) => (
            <li key={preference.id}>{this.renderPreferenceItem(preference)}</li>
          ))}
        </ul>

        {this.renderSaveButton()}

        <StatusMessage status={this.props.updateStatus.state} />
      </div>
    );
  }
}

export default compose<PropsToComponent, PropsFromCaller>(withApollo)(EmailPreferencesList);
